import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { AxiosError, AxiosResponse } from "axios";
import axios from "axios";

import { getBaseApiURL } from "@helpers/api";
import { hasKey } from "@helpers/utils";

/**
 * Base API configuration for RTK Query.
 * All API slices should inject their endpoints into this base API.
 */
export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseApiURL(),
    credentials: "include",
  }),
  tagTypes: [
    "Job",
    "Account",
    "User",
    "DataObject",
    "ImageRegistry",
    "JupyterImage",
    "Role",
    "Team",
    "AccessRequest",
    "Service",
    "Policy",
  ],
  endpoints: () => ({}),
});

type AxiosErrorWithResponse<T> = AxiosError<T> & {
  response: AxiosResponse<T>;
};

type ToolkitError = {
  code: number;
  message: string;
  status: string;
  type: string;
};

/**
 * Checks if an Axios error response contains a Toolkit API error format.
 */
function isToolkitError(
  error: AxiosErrorWithResponse<unknown>,
): error is AxiosErrorWithResponse<{ error: ToolkitError }> {
  return !error.response.data
    ? false
    : hasKey(error.response.data, "error") &&
        hasKey(error.response.data.error, "code") &&
        hasKey(error.response.data.error, "message") &&
        hasKey(error.response.data.error, "status") &&
        hasKey(error.response.data.error, "type");
}

/**
 * Helper function to handle errors in RTK Query queryFn.
 * Preserves HTTP status codes and error types when available.
 * Extracts user-friendly messages from Toolkit API error format.
 */
export function handleQueryError(error: unknown): {
  error:
    | { status: number; data: unknown; message?: string }
    | { status: "FETCH_ERROR"; error: string }
    | { status: "CUSTOM_ERROR"; error: string };
} {
  if (axios.isAxiosError(error)) {
    if (error.response) {
      const errorWithResponse = error as AxiosErrorWithResponse<unknown>;
      const message = isToolkitError(errorWithResponse)
        ? errorWithResponse.response.data.error.message
        : undefined;

      return {
        error: {
          status: error.response.status,
          data: error.response.data,
          ...(message && { message }),
        },
      };
    } else if (error.request) {
      return {
        error: {
          status: "FETCH_ERROR" as const,
          error: "Network error: No response received",
        },
      };
    }
  }

  // Fall back to custom error for anything else
  return {
    error: {
      status: "CUSTOM_ERROR" as const,
      error: String(error),
    },
  };
}

/**
 * Extracts a user-friendly error message from an RTK Query error.
 * Handles both API errors (with `message`) and network errors (with `error`).
 */
export function getErrorMessage(error: unknown): string | null {
  if (!error || typeof error !== "object") return null;
  if ("message" in error) return (error as { message: string }).message;
  if ("error" in error) return (error as { error: string }).error;
  return null;
}
